import { styled } from '@mui/material';

// Base styles that can be overridden by styled(JsonViewer)
const ViewerRoot = styled('div')(({ theme }) => ({
    "& .json-property": {
        marginLeft: theme.spacing(2),
    },
    '& .json-key': {
        color: theme.palette.text.primary,
    },
    "& .json-string": {
        color: theme.palette.success.main,
    },
    "& .json-number": {
        color: theme.palette.info.main,
    },
    "& .json-boolean": {
        color: theme.palette.info.main,
    },
    "& .json-null": {
        color: theme.palette.text.secondary,
    },
    "& .json-undefined": {
        color: theme.palette.text.secondary,
    },
    "& .json-error": {
        color: theme.palette.error.main,
    },
}));

interface JsonViewerProps {
    value?: string | object;
    className?: string;
}

const JsonViewer: React.FC<JsonViewerProps> = ({ value, className }) => {
    const renderValue = (val: any): JSX.Element => {
        if (val === null) return <span className="json-null">null</span>;
        if (val === undefined) return <span className="json-undefined">undefined</span>;
        if (typeof val === 'string') return <span className="json-string">"{val}"</span>;
        if (typeof val === 'number') return <span className="json-number">{val}</span>;
        if (typeof val === 'boolean') return <span className="json-boolean">{val.toString()}</span>;
        if (Array.isArray(val)) {
            return (
                <span>[
                    {val.map((item, index) => (
                        <span key={index}>
                            {renderValue(item)}
                            {index < val.length - 1 && ', '}
                        </span>
                    ))}
                ]</span>
            );
        }
        if (typeof val === 'object') {
            return (
                <div className="json-object">
                    {'{'}
                    {Object.entries(val).map(([key, value], index, array) => (
                        <div key={key} className="json-property">
                            <span>&#34;<span className="json-key">{key}</span>&#34;</span>: {renderValue(value)}
                            {index < array.length - 1 && ','}
                        </div>
                    ))}
                    {'}'}
                </div>
            );
        }
        return <span>{String(val)}</span>;
    };

    const parseAndRender = () => {
        try {
            if (value) {
                const data = typeof value === 'string' ? JSON.parse(value) : value;
                return renderValue(data);
            }
            return null;
        } catch (e) {
            return <span className="json-error">Invalid JSON: {String(value)}</span>;
        }
    };

    return (
        <ViewerRoot className={`JsonViewer-root ${className || ''}`}>
            {parseAndRender()}
        </ViewerRoot>
    );
};

export default JsonViewer;
